/**
 * Where this session has just been, kept for exactly as long as it takes to
 * report a problem.
 *
 * Judgement J5 (`docs\plans\2026-08-05-overnight-decisions.md`): the
 * server-side audit trail of worklist item 22 does not exist yet, so a
 * feedback entry carries the client's own recent navigation instead. That is
 * what turns "the wizard confused me" into a sequence somebody can walk: which
 * screen they were on, and which screens they had just come from, when they
 * gave up. When item 22 lands, the two join on user and time window.
 *
 * Three properties are deliberate and each is pinned in `trail.test.ts`:
 *
 * - **In memory only.** Nothing here touches `localStorage`, `sessionStorage`
 *   or a cookie. A record of a colleague's movements through an HR system is
 *   not something to leave on their machine, and it has no job to do after the
 *   tab closes. It rides along on one POST and nowhere else.
 * - **Bounded.** The buffer holds the last {@link TRAIL_LIMIT} steps and drops
 *   the oldest, so a person who leaves the app open all day carries a trail of
 *   twenty steps rather than one of four thousand.
 * - **Paths only, never search strings.** A query string on this product's
 *   screens carries what somebody typed into a search box, which on the
 *   employee list is a colleague's surname. The trail exists to say which
 *   screen, not to copy business data into a second table.
 *
 * Module state rather than React state on purpose: it is written from the
 * router's subscription and read at the moment a dialog is submitted, which
 * are two places that never render together, and a context would make every
 * navigation re-render the whole tree.
 */

export type TrailStep = {
  /** The route path, without its search string. */
  path: string
  /** ISO 8601, as every instant on the wire in this product is. */
  at: string
}

export type FeedbackContext = {
  trail: TrailStep[]
  /** CSS pixels. A layout complaint is unreadable without the width it happened at. */
  viewport: { w: number; h: number }
  userAgent: string
}

/** Twenty steps: J5's "last ~20 route changes". */
export const TRAIL_LIMIT = 20

let steps: TrailStep[] = []

/**
 * Note that the session moved to `path`.
 *
 * The same path twice in a row is not recorded. A re-render, a replaced search
 * string, or pressing the nav link for the screen already open all arrive here
 * as a navigation, and twenty of those would push the real trail out of a
 * twenty-step buffer - which is precisely the trail somebody would need.
 *
 * `at` is injectable so the tests can state an instant rather than compare
 * against a clock they do not control.
 */
export function recordVisit(path: string, at: Date = new Date()) {
  if (steps.at(-1)?.path === path) return
  steps.push({ path, at: at.toISOString() })
  if (steps.length > TRAIL_LIMIT) steps = steps.slice(steps.length - TRAIL_LIMIT)
}

/**
 * The trail, oldest step first, as a copy.
 *
 * A copy because the array is module state: handing the live one out lets any
 * caller edit the record of where somebody has been, which is the one thing
 * this buffer must not allow.
 */
export function readTrail(): TrailStep[] {
  return steps.map((step) => ({ ...step }))
}

/** Everything the dialog sends beside the words. */
export function feedbackContext(): FeedbackContext {
  return {
    trail: readTrail(),
    viewport: { w: window.innerWidth, h: window.innerHeight },
    userAgent: navigator.userAgent,
  }
}

/** Start again. Used by the tests; a sign-out clears it by reloading the app. */
export function clearTrail() {
  steps = []
}
